/**
 * Emello Triple Whale Custom Events
 * ──────────────────────────────────
 * Asset: emello-triple-whale-events.js
 *
 * Fires four custom funnel events into Triple Whale:
 *
 *   emello_add_to_cart_clicked    — customer clicked an ATC button (intent)
 *   emello_add_to_cart_success    — Shopify confirmed the item was added
 *   emello_cart_drawer_opened     — cart drawer became visible
 *   emello_begin_checkout_clicked — customer clicked the checkout CTA
 *
 * Load order: included in theme.liquid after the Triple Whale app embed block.
 * Triple Whale is loaded via Shopify app embed — window.TriplePixel may not
 * be available immediately. The queue mechanism below handles late loading.
 *
 * Debug mode: set window.EMELLO_TW_DEBUG = true in the browser console.
 */

(function () {
  'use strict';

  // ─── 1. Namespace ────────────────────────────────────────────────────────────

  window.EmelloTW = window.EmelloTW || {};

  // ─── 2. Debug logger ─────────────────────────────────────────────────────────

  function log(level, message, data) {
    if (window.EMELLO_TW_DEBUG !== true) return;
    var fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    data !== undefined
      ? fn('[Emello TW] ' + message, data)
      : fn('[Emello TW] ' + message);
  }

  // ─── 3. Event queue (handles TriplePixel loading late) ───────────────────────

  window.EmelloTW.queue = window.EmelloTW.queue || [];

  window.EmelloTW.fire = function (eventName, payload) {
    if (typeof window.TriplePixel === 'function') {
      try {
        window.TriplePixel('custom', eventName, payload || {});
        log('log', 'fired: ' + eventName, payload);
      } catch (err) {
        log('error', 'fire failed: ' + eventName, err);
      }
    } else {
      window.EmelloTW.queue.push({ eventName: eventName, payload: payload || {}, at: Date.now() });
      log('warn', 'queued (TriplePixel not ready): ' + eventName, payload);
    }
  };

  // Flush queue once TriplePixel is available, give up after 10 seconds
  var flushInterval = setInterval(function () {
    if (typeof window.TriplePixel !== 'function') return;
    while (window.EmelloTW.queue.length > 0) {
      var item = window.EmelloTW.queue.shift();
      try {
        window.TriplePixel('custom', item.eventName, item.payload);
        log('log', 'flushed: ' + item.eventName, item.payload);
      } catch (err) {
        log('error', 'flush failed: ' + item.eventName, err);
      }
    }
    clearInterval(flushInterval);
  }, 500);

  setTimeout(function () { clearInterval(flushInterval); }, 10000);

  // ─── 4. Helper: page type ─────────────────────────────────────────────────────

  window.EmelloTW.getPageType = function () {
    var path = window.location.pathname;
    if (path === '/') return 'homepage';
    if (path.indexOf('/products/') === 0) return 'pdp';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path.indexOf('/cart') === 0) return 'cart';
    if (path.indexOf('/search') === 0) return 'search';
    return 'unknown';
  };

  // ─── 5. Helper: item count group ─────────────────────────────────────────────

  window.EmelloTW.getItemCountGroup = function (count) {
    var n = Number(count || 0);
    if (n <= 0) return 'empty';
    if (n === 1) return 'one_item';
    if (n === 2) return 'two_items';
    if (n >= 3) return 'three_plus_items';
    return 'unknown';
  };

  // ─── 6. Helper: get cart item count from Alpine store ────────────────────────
  //
  // The Emello theme manages cart state entirely through Alpine.store('cart').
  // DOM-based count selectors (data-cart-count, .cart-count-bubble) do not
  // exist in this theme. Reading from the Alpine store is the reliable source.

  window.EmelloTW.getCartItemCount = function () {
    try {
      var cartStore = window.Alpine && window.Alpine.store('cart');
      if (cartStore && cartStore.object && typeof cartStore.object.item_count === 'number') {
        return cartStore.object.item_count;
      }
    } catch (e) {
      log('warn', 'could not read cart item count from Alpine store');
    }
    return 0;
  };

  // ─── 7. Helper: safe string ───────────────────────────────────────────────────

  function str(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  // ─── 8. Deduplication for add_to_cart_success ────────────────────────────────
  //
  // The theme uses fetch() directly in cart.add(). We hook into this via a
  // fetch wrapper (Event 2a) and also listen for a theme-level custom event
  // if one is dispatched in future. The dedupe window prevents double-firing
  // if both mechanisms ever fire at the same time.

  window.EmelloTW._lastSuccessKey = null;
  window.EmelloTW._lastSuccessAt = 0;

  window.EmelloTW.fireAddToCartSuccess = function (payload) {
    var now = Date.now();
    var key = [
      str(payload.variant_id),
      str(payload.quantity_added),
      str(payload.source_component)
    ].join('|');

    if (key === window.EmelloTW._lastSuccessKey && now - window.EmelloTW._lastSuccessAt < 1500) {
      log('warn', 'duplicate add_to_cart_success suppressed', payload);
      return;
    }

    window.EmelloTW._lastSuccessKey = key;
    window.EmelloTW._lastSuccessAt = now;
    window.EmelloTW.fire('emello_add_to_cart_success', payload);
  };

  // ─── 9. Deduplication for cart_drawer_opened ─────────────────────────────────

  window.EmelloTW._lastDrawerOpenAt = 0;

  window.EmelloTW.fireCartDrawerOpened = function (trigger) {
    var now = Date.now();
    if (now - window.EmelloTW._lastDrawerOpenAt < 1500) {
      log('warn', 'duplicate cart_drawer_opened suppressed');
      return;
    }
    window.EmelloTW._lastDrawerOpenAt = now;

    var count = window.EmelloTW.getCartItemCount();

    window.EmelloTW.fire('emello_cart_drawer_opened', {
      page_type: 'cart_drawer',
      source_component: 'cart_drawer',
      drawer_trigger: str(trigger) || 'unknown',
      cart_state: count > 0 ? 'has_items' : 'empty',
      item_count_group: window.EmelloTW.getItemCountGroup(count)
    });
  };

  // ─── 10. EVENT 1: emello_add_to_cart_clicked ─────────────────────────────────
  //
  // The Emello theme does not use name="add" or type="submit" on ATC buttons.
  // PDP and sticky ATC buttons use class .pdp-info__add-to-cart.
  // PLP quick-add (single variant) uses class .product-card__quick-add.
  // PLP quick-add (multi-variant) opens the size selector drawer — the
  //   add_to_cart_clicked event fires here at drawer open, not at final confirm.
  //   This is intentional: it measures initial intent from the PLP.
  //
  // source_component is set via data-emello-source on each button (see
  // main-product.liquid and header-sticky-atc.liquid).

  document.addEventListener('click', function (event) {
    var button = event.target.closest(
      '.pdp-info__add-to-cart, .product-card__quick-add, [data-emello-atc]'
    );
    if (!button) return;

    // Do not fire for disabled buttons
    if (button.disabled || button.getAttribute('disabled') !== null) return;

    var sourceComponent = str(button.getAttribute('data-emello-source')) || 'unknown';
    var pageType = window.EmelloTW.getPageType();

    // For PDP/sticky buttons, read variant from Alpine store (reliable source)
    // For PLP quick-add, read from data attributes on the button/card
    var productId = '';
    var variantId = '';
    var productTitle = '';
    var variantTitle = '';
    var productType = '';
    var size = '';

    if (
      button.classList.contains('pdp-info__add-to-cart') ||
      sourceComponent === 'pdp_add_to_cart' ||
      sourceComponent === 'sticky_add_to_cart'
    ) {
      try {
        var pdpStore = window.Alpine && window.Alpine.store('pdp_size_selector');
        var variant = pdpStore && pdpStore.selected;
        if (variant) {
          variantId = str(variant.id);
          variantTitle = str(variant.title);
          size = str(variant.title); // title is the size in this theme
        }
        // product_variants is set on window.theme for PDP pages
        if (window.theme && window.theme.product_variants && window.theme.product_variants[0]) {
          productId = str(window.theme.product_variants[0].product_id);
        }
        productTitle = str(document.querySelector('h1.pdp-info__title') && document.querySelector('h1.pdp-info__title').textContent);
        productType = str(document.querySelector('[data-product-type]') && document.querySelector('[data-product-type]').getAttribute('data-product-type'));
      } catch (e) {
        log('warn', 'could not read PDP Alpine store for clicked payload', e);
      }
    } else {
      // PLP quick-add: read from data attributes set in product-card.liquid
      productId = str(button.getAttribute('data-product-id'));
      variantId = str(button.getAttribute('data-variant-id'));
      productTitle = str(button.getAttribute('data-product-title'));
      variantTitle = str(button.getAttribute('data-variant-title'));
      productType = str(button.getAttribute('data-product-type'));
    }

    window.EmelloTW.fire('emello_add_to_cart_clicked', {
      product_id: productId,
      variant_id: variantId,
      product_title: productTitle,
      variant_title: variantTitle,
      size: size,
      colour: '',
      product_type: productType,
      page_type: pageType,
      source_component: sourceComponent,
      quantity_attempted: 1
    });

  }, true);

  // ─── 11. EVENT 2: emello_add_to_cart_success ─────────────────────────────────
  //
  // Hook into the theme's cart.add() fetch call to /cart/add.js.
  // This is the only path used by all ATC buttons in this theme:
  //   - PDP button     → $store.pdp_size_selector.addToCart() → cart.add()
  //   - Sticky ATC     → $store.pdp_size_selector.addToCart() → cart.add()
  //   - PLP single var → $store.cart.add(variantId) directly
  //   - PLP multi var  → $store.global_size_selector.addToCart() → cart.add()
  //
  // We wrap window.fetch once and check the URL against cartAdd route.
  // Guard flag prevents double-wrapping on section re-renders.

  if (!window.EmelloTW._fetchWrapped) {
    window.EmelloTW._fetchWrapped = true;

    var _originalFetch = window.fetch;

    window.fetch = function () {
      var args = arguments;
      var requestUrl = '';

      try {
        requestUrl = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      } catch (e) {}

      var cartAddRoute = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
      var isCartAdd = requestUrl === cartAddRoute || requestUrl.indexOf('/cart/add') !== -1;

      return _originalFetch.apply(this, args).then(function (response) {
        if (!isCartAdd || !response || !response.ok) return response;

        response.clone().json().then(function (data) {
          // Shopify returns the added line item object on success
          // data.id is the variant_id, data.product_id is the product
          var payload = {
            product_id: str(data.product_id),
            variant_id: str(data.variant_id || data.id),
            product_title: str(data.product_title || data.title),
            variant_title: str(
              data.variant_title ||
              (data.variant_options && data.variant_options.join(' / ')) ||
              ''
            ),
            size: str(
              data.variant_options && data.variant_options[0]
                ? data.variant_options[0]
                : ''
            ),
            colour: '',
            product_type: str(data.product_type),
            page_type: window.EmelloTW.getPageType(),
            source_component: 'cart_add_js',
            quantity_added: Number(data.quantity || 1)
          };

          window.EmelloTW.fireAddToCartSuccess(payload);

        }).catch(function () {
          // Response body could not be parsed — fire with minimal payload
          window.EmelloTW.fireAddToCartSuccess({
            product_id: '',
            variant_id: '',
            product_title: '',
            variant_title: '',
            size: '',
            colour: '',
            product_type: '',
            page_type: window.EmelloTW.getPageType(),
            source_component: 'cart_add_js',
            quantity_added: 1
          });
        });

        return response;
      });
    };
  }

  // ─── 12. EVENT 3: emello_cart_drawer_opened ──────────────────────────────────
  //
  // The Emello theme controls drawer visibility entirely through
  // Alpine.store('drawer').current. When current === 'cart' the drawer is open.
  //
  // A MutationObserver approach caused repeated suppression warnings in QA
  // because Alpine makes many DOM mutations while rendering the drawer contents,
  // each one re-triggering the observer. Instead we poll the Alpine store at
  // 200ms and fire exactly once on the closed-to-open transition. This produces
  // a single clean event regardless of how many DOM updates Alpine makes.

  (function () {

    var cartDrawerWasOpen = false;

    function isCartDrawerOpen() {
      try {
        var drawerStore = window.Alpine && window.Alpine.store('drawer');
        return drawerStore && drawerStore.current === 'cart';
      } catch (e) {
        return false;
      }
    }

    // Poll at 200ms — fast enough to feel immediate, cheap enough to be safe.
    // Fires only on the closed → open state transition.
    setInterval(function () {
      var isOpen = isCartDrawerOpen();
      if (isOpen && !cartDrawerWasOpen) {
        window.EmelloTW.fireCartDrawerOpened('drawer_state_change');
      }
      cartDrawerWasOpen = isOpen;
    }, 200);

  })();

  // ─── 13. EVENT 4: emello_begin_checkout_clicked ──────────────────────────────
  //
  // The checkout button in cart-drawer.liquid is:
  //   <a href="...checkout..." class="btn btn--black btn--no-max">
  // There is no name="checkout" attribute. We match on the checkout path
  // within the cart drawer footer to avoid matching other links.

  document.addEventListener('click', function (event) {
    var checkoutLink = event.target.closest(
      '.cart-drawer__footer-buttons a[href*="/checkout"], [data-emello-checkout]'
    );
    if (!checkoutLink) return;

    var count = window.EmelloTW.getCartItemCount();

    window.EmelloTW.fire('emello_begin_checkout_clicked', {
      page_type: 'cart_drawer',
      source_component: 'cart_drawer_checkout_button',
      cart_state: count > 0 ? 'has_items' : 'empty',
      item_count_group: window.EmelloTW.getItemCountGroup(count),
      promo_state: 'unknown'
    });
  }, true);

})();